import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { PasswordField } from '../components/PasswordField';
import { useAuth } from '../hooks/useAuth';
import { BRAND_LOGO } from '../components/GreetingOverlay';
import { LiveBackground } from '../components/LiveBackground';
import { api } from '../lib/api';
import type { LoginUser } from '../lib/types';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [selected, setSelected] = useState<LoginUser | null>(null);
  // Hosť (pozvaný do jedného projektu) vo verejnom výbere nie je — výber je
  // pre tím a mená klientov z internetu vidieť nemajú. Prihlási sa menom.
  const [guestLogin, setGuestLogin] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['auth', 'users'],
    queryFn: () => api.authUsers(),
    staleTime: 30_000,
  });

  const users = usersQuery.data?.users ?? [];

  // The clock in the logo shows the real time at mount; the greeting follows
  // the time of day. Small touches — the app is about time, after all.
  const { hourDeg, minuteDeg, greeting } = useMemo(() => {
    const now = new Date();
    const h = now.getHours();
    return {
      hourDeg: (h % 12) * 30 + now.getMinutes() * 0.5,
      minuteDeg: now.getMinutes() * 6,
      greeting: h < 4 ? 'Dobrý večer' : h < 10 ? 'Dobré ráno' : h < 18 ? 'Dobrý deň' : 'Dobrý večer',
    };
  }, []);

  const reset = () => {
    setSelected(null);
    setGuestLogin(false);
    setPassword('');
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const username = guestLogin ? guestName.trim() : selected?.name;
    if (!username) {
      if (guestLogin) toast.error('Zadaj svoje meno.');
      return;
    }

    if (!password) {
      toast.error('Zadaj heslo.');
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-5 py-10">
      {/* Statické pozadie vzhľadu + jedno svetlo akcentu. Žiadne nekonečné
          animácie ani filter: blur na veľkých vrstvách — prihlasovacia
          obrazovka môže byť otvorená celý deň na zdieľanom počítači. */}
      <LiveBackground />
      <div aria-hidden="true" className="login-glow" />

      <div className={`w-full rise ${selected || guestLogin ? 'max-w-md' : 'max-w-2xl'}`}>
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="relative mb-5">
            <span
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] bg-brand-500/20 blur-2xl dark:bg-brand-500/25"
            />
            <img
              src={BRAND_LOGO}
              alt="ONVI WORKS"
              className="relative mx-auto h-[38px] w-auto"
            />
          </span>
          {/* Písmo nadpisov vzhľadu — v Ateliéri patkové, ako všade inde. */}
          <p className="mt-1.5 text-[17px] text-slate-600 dark:text-slate-300" style={{ fontFamily: 'var(--ww-font-headline)' }}>
            {selected ? selected.name : guestLogin ? 'Prihlásenie hosťa' : `${greeting} — vyber svoj profil`}
          </p>

        </div>

        {guestLogin ? (
          <form className="panel login-pop p-6" onSubmit={submit}>
            <p className="mb-4 text-center text-sm text-slate-500 dark:text-slate-400">
              Prihlás sa menom, ktoré si zvolil pri prijatí pozvánky.
            </p>
            <label className="label" htmlFor="guestLoginName">
              Meno
            </label>
            <input
              id="guestLoginName"
              className="input mb-4"
              value={guestName}
              onChange={(event) => setGuestName(event.target.value)}
              autoComplete="username"
              disabled={loading}
              autoFocus
            />
            <PasswordField
              id="guestLoginPassword"
              label="Heslo"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              placeholder="••••••••"
              disabled={loading}
            />
            <button className="btn-primary mt-5 w-full py-2.5" type="submit" disabled={loading}>
              <LockClosedIcon className="mr-1.5 h-4 w-4" />
              {loading ? 'Moment…' : 'Prihlásiť sa'}
            </button>
            <button
              type="button"
              onClick={reset}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              disabled={loading}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Späť na výber profilu
            </button>
          </form>
        ) : !selected ? (
          // Apple-TV style profile picker: avatars side by side on the ambient
          // background, no surrounding box.
          usersQuery.isError ? (
            <div className="px-4 py-8 text-center">
              <p className="text-[15px] text-slate-500 dark:text-slate-400">
                Nepodarilo sa načítať používateľov.
              </p>
              <button
                className="btn-secondary mt-3"
                type="button"
                onClick={() => void usersQuery.refetch()}
              >
                Skúsiť znova
              </button>
            </div>
          ) : usersQuery.isLoading ? (
            <div className="flex flex-wrap items-start justify-center gap-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-3">
                  <div className="skeleton h-28 w-28 rounded-full" />
                  <div className="skeleton h-4 w-16" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">Žiadni používatelia.</p>
          ) : (
            <div className="flex flex-wrap items-start justify-center gap-6 sm:gap-10">
              {users.map((u, i) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => {
                    setSelected(u);
                    setPassword('');
                                  }}
                  style={{ animationDelay: `${120 + i * 80}ms` }}
                  className="login-pop group flex w-32 flex-col items-center gap-3 rounded-2xl p-2 outline-none transition duration-300 hover:-translate-y-1 focus-visible:-translate-y-1 active:scale-[0.97]"
                >
                  <span className="relative inline-flex rounded-full p-[3px] transition duration-300 group-hover:scale-[1.06] group-focus-visible:scale-[1.06]">
                    {/* glow + gradient ring, revealed on hover/keyboard focus */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 rounded-full bg-brand-500/50 opacity-0 blur-xl transition duration-300 group-hover:opacity-70 group-focus-visible:opacity-70"
                    />
                    <span
                      aria-hidden="true"
                      className="login-ring absolute inset-0 rounded-full opacity-0 transition duration-300 group-hover:opacity-100 group-focus-visible:opacity-100"
                    />
                    <span className="relative block h-24 w-24 overflow-hidden rounded-full shadow-ios ring-1 ring-black/[0.06] dark:ring-white/15">
                      <Avatar name={u.name} src={u.avatarUrl} size="xl" />
                    </span>
                  </span>
                  <span className="text-[16px] font-semibold leading-tight tracking-tight text-slate-700 transition duration-300 group-hover:text-slate-950 dark:text-slate-200 dark:group-hover:text-white">
                    {u.name}
                  </span>
                  <span className="-mt-1.5 text-[11px] text-slate-500 transition duration-300 group-hover:text-slate-600 dark:text-slate-400 dark:group-hover:text-slate-300">
                    prihlásiť sa
                  </span>
                </button>
              ))}
            </div>
          )
        ) : (
          <form
            className="panel login-pop p-6"
            onSubmit={submit}
          >
            <div className="mb-4 flex flex-col items-center gap-2 text-center">
              <span className="login-ring inline-flex h-[102px] w-[102px] shrink-0 items-center justify-center rounded-full p-[3px] shadow-ios">
                <span className="block h-24 w-24 shrink-0 overflow-hidden rounded-full">
                  <Avatar name={selected.name} src={selected.avatarUrl} size="xl" />
                </span>
              </span>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Zadaj svoje heslo.
              </p>
            </div>

            <PasswordField
              id="password"
              label="Heslo"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              placeholder="••••••••"
              disabled={loading}
              autoFocus
            />

            <button className="btn-primary mt-5 w-full py-2.5" type="submit" disabled={loading}>
              <LockClosedIcon className="mr-1.5 h-4 w-4" />
              {loading ? 'Moment…' : 'Prihlásiť sa'}
            </button>

            <button
              type="button"
              onClick={reset}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              disabled={loading}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Iný profil
            </button>
          </form>
        )}
      </div>

      {!selected && !guestLogin ? (
        <button
          type="button"
          onClick={() => {
            setGuestLogin(true);
            setPassword('');
          }}
          className="mt-8 text-sm font-medium text-slate-500 underline-offset-4 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
        >
          Som hosť — prihlásiť sa menom
        </button>
      ) : null}

      <p className="pointer-events-none absolute inset-x-0 bottom-5 text-center text-[11px] tracking-wide text-slate-500 dark:text-slate-400">
        Interný systém · ONVI s.r.o.
      </p>
    </div>
  );
};
